import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { isNil } from 'lodash';
import { DoenKidsGenericApiProvider } from 'src/providers/generic.provider';
import { IOrganizationUnitVision } from 'typings/doenkids/doenkids';
import { IServiceResponse } from 'typings/api-generic';

/**
 * The pedagogical vision of a customer organization unit.
 *
 * Every route takes the organization unit the user is WORKING IN and resolves
 * the owning customer itself, so callers pass their current OU and never a
 * resolved customer id. A unit with no customer ancestor answers 404.
 *
 * Reading needs READ on the unit; writing and archiving need organization
 * management permission. This is org-admin territory, not DoenKids-only.
 */
@Injectable({
  providedIn: 'root',
})
export class OrganizationUnitVisionService {
  constructor(private $baseApi: DoenKidsGenericApiProvider) {}

  /**
   * Fetches the vision, or null when none is stored.
   *
   * The API answers 204 for "no vision", which HttpClient surfaces as a null
   * body. An archived vision reads as no vision at all.
   */
  async fetch(organizationUnitId: number): Promise<IOrganizationUnitVision | null> {
    const vision = (await firstValueFrom(
      this.$baseApi.genericGetCallWithoutErrorHandler(`/customer/organization-unit/${organizationUnitId}/vision`),
    )) as IOrganizationUnitVision | null;

    return isNil(vision) ? null : vision;
  }

  /**
   * Stores the vision. Revives an archived row rather than adding a second one.
   *
   * The API rejects an empty string, so clearing the vision is `archive()`,
   * not an update with ''.
   */
  async update(organizationUnitId: number, vision: string) {
    return (await firstValueFrom(
      this.$baseApi.genericPutCall(`/customer/organization-unit/${organizationUnitId}/vision`, { vision }),
    )) as IOrganizationUnitVision;
  }

  /** Archives the vision. Answers 204 when there was nothing to archive. */
  async archive(organizationUnitId: number) {
    return (await firstValueFrom(
      this.$baseApi.genericDeleteCall(`/customer/organization-unit/${organizationUnitId}/vision`),
    )) as IServiceResponse;
  }
}
